import { supabase } from "@/lib/supabase";
import { logger } from "@/lib/logger";
import { logWoundApproval } from '@/services/wound-audit.service';
import { getResidentsByIds } from '@/services/resident.service';

// ─── Types (aligned with mobile woundService.ts) ───────────────────────────

export type WoundDiagnosis =
    | "dekubitus_grad_1"
    | "dekubitus_grad_2"
    | "dekubitus_grad_3"
    | "dekubitus_grad_4"
    | "ulcus_cruris"
    | "diabetisches_ulcus"
    | "operationswunde"
    | "traumatische_wunde"
    | "verbrennungswunde"
    | "skin_tear"
    | "intertrigo"
    | "andere";

export type WoundUrgency = "green" | "yellow" | "red" | "critical";
export type HealingStatus = "verbessert" | "unveraendert" | "verschlechtert" | "neu" | "abgeheilt";

export type WoundEdge = "flach" | "aufgeworfen" | "unterminiert" | "mazeriert" | "nekrotisch";
export type WoundBed = "granulation_rot" | "fibrin_gelb" | "nekrose_schwarz" | "epithel_rosa" | "gemischt";
export type ExudateAmount = "keine" | "gering" | "maessig" | "stark";
export type ExudateType = "seroes" | "sanguinolent" | "purulent" | "seroes_purulent";
export type WoundOdor = "kein" | "leicht" | "stark" | "uebel";
export type SurroundingSkin = "intakt" | "geroetet" | "mazeriert" | "trocken_schuppig" | "oedematoes";
export type ApprovalAction = "approved" | "rejected" | "modified" | "delegated" | "revoked";

export type WoundBodyLocation =
    | "kopf_gesicht"
    | "stirn"
    | "gesicht"
    | "ohr_links"
    | "ohr_rechts"
    | "nacken"
    | "schulter_rechts"
    | "schulter_links"
    | "oberarm_rechts"
    | "oberarm_links"
    | "unterarm_rechts"
    | "unterarm_links"
    | "hand_rechts"
    | "hand_links"
    | "finger_rechts"
    | "finger_links"
    | "brust"
    | "bauch"
    | "ruecken_oben"
    | "ruecken_mitte"
    | "ruecken_unten"
    | "hueftbereich_rechts"
    | "hueftbereich_links"
    | "gesaess_rechts"
    | "gesaess_links"
    | "steissbein"
    | "leiste_rechts"
    | "leiste_links"
    | "oberschenkel_rechts"
    | "oberschenkel_links"
    | "knie_rechts"
    | "knie_links"
    | "unterschenkel_rechts"
    | "unterschenkel_links"
    | "knoechel_rechts"
    | "knoechel_links"
    | "ferse_rechts"
    | "ferse_links"
    | "fuss_rechts"
    | "fuss_links"
    | "zehen_links"
    | "zehen_rechts"
    | "arm_links"
    | "arm_rechts"
    | "andere";

export interface ProductUsage {
    productId?: string;
    productName: string;
    productCategory: string;
    quantity?: number;
    notes?: string;
}

export interface WoundAssessment {
    id: string;
    facilityId: string;
    residentId: string;
    residentName: string;
    woundId: string | null;
    diagnosis: WoundDiagnosis;
    diagnosisName: string | null;
    bodyLocation: WoundBodyLocation;
    locationDetail: string | null;
    urgency: WoundUrgency;
    healingStatus: HealingStatus;
    lengthCm: number | null;
    widthCm: number | null;
    depthCm: number | null;
    areaCm2: number | null;
    woundBed: WoundBed | null;
    woundEdge: WoundEdge | null;
    exudateAmount: ExudateAmount | null;
    exudateType: ExudateType | null;
    woundOdor: WoundOdor | null;
    surroundingSkin: SurroundingSkin | null;
    painLevel: number | null;
    infectionSigns: boolean;
    treatmentNotes: string | null;
    treatmentPerformed: string | null;
    dressingUsed: string | null;
    nextChangeDate: string | null;
    description: string | null;
    assessedBy: string;
    assessedByName: string;
    photoUrl: string | null;
    photoUrls: string[];
    productsUsed: ProductUsage[];
    notifyDoctor: boolean;
    doctorConfirmed: boolean;
    doctorConfirmedAt: string | null;
    approvalStatus: "pending" | "approved" | "rejected" | null;
    approvedBy: string | null;
    recordedAt: string;
    createdAt: string;
    updatedAt: string;
}

export interface CreateWoundInput {
    facilityId: string;
    residentId: string;
    residentName: string;
    woundId?: string | null;
    diagnosis: WoundDiagnosis;
    diagnosisName?: string | null;
    bodyLocation: WoundBodyLocation;
    locationDetail?: string | null;
    urgency: WoundUrgency;
    healingStatus: HealingStatus;
    lengthCm?: number | null;
    widthCm?: number | null;
    depthCm?: number | null;
    woundBed?: WoundBed | null;
    woundEdge?: WoundEdge | null;
    exudateAmount?: ExudateAmount | null;
    exudateType?: ExudateType | null;
    woundOdor?: WoundOdor | null;
    surroundingSkin?: SurroundingSkin | null;
    painLevel?: number | null;
    infectionSigns?: boolean;
    treatmentNotes?: string | null;
    treatmentPerformed?: string | null;
    dressingUsed?: string | null;
    nextChangeDate?: string | null;
    description?: string | null;
    photoUrls?: string[];
    productsUsed?: ProductUsage[];
    notifyDoctor?: boolean;
}

export interface WoundApprovalInput {
    assessmentId: string;
    facilityId: string;
    action: ApprovalAction;
    approverName: string;
    approverStaffId?: string | null;
    approverUserId?: string | null;
    approverRole?: string | null;
    approverLanr?: string;
    approverSpecialty?: string;
    diagnosisCode?: WoundDiagnosis;
    diagnosisName?: string;
    oldStatus?: string | null;
    approvalReason?: string | null;
    notes?: string;
}

export interface WoundTrendPoint {
    date: string;
    lengthCm: number | null;
    widthCm: number | null;
    depthCm: number | null;
    areaCm2: number | null;
    painLevel: number | null;
    healingStatus: HealingStatus;
}

type WoundRecord = Record<string, unknown>;

function readDiagnosisCode(row: WoundRecord): WoundDiagnosis {
    return ((row.diagnosis as WoundDiagnosis | undefined)
        ?? (row.diagnosis_code as WoundDiagnosis | undefined)
        ?? 'andere');
}

function encodeKeyPart(value: unknown): string {
    return encodeURIComponent(String(value ?? '').trim());
}

function decodeKeyPart(value: string): string {
    return decodeURIComponent(value);
}

function buildDerivedWoundId(row: WoundRecord): string {
    const residentId = encodeKeyPart(row.resident_id);
    const diagnosis = encodeKeyPart(readDiagnosisCode(row));
    const bodyLocation = encodeKeyPart(row.body_location);
    const bodyLocationDetail = encodeKeyPart(row.body_location_detail);

    return `derived:${residentId}:${diagnosis}:${bodyLocation}:${bodyLocationDetail || 'na'}`;
}

function parseDerivedWoundId(woundId: string): {
    residentId: string;
    diagnosis: WoundDiagnosis;
    bodyLocation: WoundBodyLocation;
    bodyLocationDetail: string | null;
} | null {
    if (!woundId.startsWith('derived:')) {
        return null;
    }

    const [, residentId = '', diagnosis = 'andere', bodyLocation = 'andere', bodyLocationDetail = 'na'] = woundId.split(':');

    return {
        residentId: decodeKeyPart(residentId),
        diagnosis: decodeKeyPart(diagnosis) as WoundDiagnosis,
        bodyLocation: decodeKeyPart(bodyLocation) as WoundBodyLocation,
        bodyLocationDetail: bodyLocationDetail === 'na' ? null : decodeKeyPart(bodyLocationDetail),
    };
}

function deriveApprovalStatus(row: WoundRecord): WoundAssessment['approvalStatus'] {
    if (row.approval_status === 'approved' || row.approval_status === 'pending' || row.approval_status === 'rejected') {
        return row.approval_status;
    }

    if (row.doctor_confirmed === true) {
        return 'approved';
    }

    if (row.notify_doctor === true) {
        return 'pending';
    }

    return null;
}

function deriveResidentName(row: WoundRecord): string {
    const residentName = row.resident_name;
    if (typeof residentName === 'string' && residentName.trim().length > 0) {
        return residentName;
    }

    const firstName = typeof row.resident_first_name === 'string' ? row.resident_first_name.trim() : '';
    const lastName = typeof row.resident_last_name === 'string' ? row.resident_last_name.trim() : '';
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();

    return fullName || 'Unbekannt';
}

function deriveAssessedByName(row: WoundRecord): string {
    const explicitName = row.assessed_by_name;
    if (typeof explicitName === 'string' && explicitName.trim().length > 0) {
        return explicitName;
    }

    const firstName = typeof row.recorded_by_first_name === 'string' ? row.recorded_by_first_name.trim() : '';
    const lastName = typeof row.recorded_by_last_name === 'string' ? row.recorded_by_last_name.trim() : '';
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();

    return fullName || 'Pflegekraft';
}

async function enrichResidentNames(rows: WoundRecord[]): Promise<WoundRecord[]> {
    const residentIds = Array.from(new Set(rows.map((row) => row.resident_id).filter((value): value is string => typeof value === 'string' && value.length > 0)));
    if (residentIds.length === 0) {
        return rows;
    }

    const residents = await getResidentsByIds(residentIds);
    const residentMap = new Map(residents.map((resident) => [resident.id, `${resident.firstName} ${resident.lastName}`.trim()]));

    return rows.map((row) => {
        if (typeof row.resident_name === 'string' && row.resident_name.trim().length > 0) {
            return row;
        }

        const residentId = typeof row.resident_id === 'string' ? row.resident_id : null;
        const residentName = residentId ? residentMap.get(residentId) : null;
        if (!residentName) {
            return row;
        }

        return {
            ...row,
            resident_name: residentName,
        };
    });
}

async function enrichAssessedByNames(rows: WoundRecord[]): Promise<WoundRecord[]> {
    const staffIds = Array.from(new Set(rows
        .map((row) => row.assessed_by ?? row.recorded_by)
        .filter((value): value is string => typeof value === 'string' && value.length > 0)));

    if (staffIds.length === 0) {
        return rows;
    }

    const [{ data: directStaff, error: directError }, { data: userLinkedStaff, error: userLinkedError }] = await Promise.all([
        supabase.from('care_staff').select('id, first_name, last_name').in('id', staffIds),
        supabase.from('care_staff').select('user_id, first_name, last_name').in('user_id', staffIds),
    ]);

    if (directError) throw new Error(directError.message);
    if (userLinkedError) throw new Error(userLinkedError.message);

    const staffMap = new Map<string, string>();
    for (const entry of (directStaff ?? []) as WoundRecord[]) {
        if (typeof entry.id === 'string') {
            staffMap.set(entry.id, [entry.first_name, entry.last_name].filter((value): value is string => typeof value === 'string' && value.length > 0).join(' '));
        }
    }
    for (const entry of (userLinkedStaff ?? []) as WoundRecord[]) {
        if (typeof entry.user_id === 'string') {
            staffMap.set(entry.user_id, [entry.first_name, entry.last_name].filter((value): value is string => typeof value === 'string' && value.length > 0).join(' '));
        }
    }

    return rows.map((row) => {
        if (typeof row.assessed_by_name === 'string' && row.assessed_by_name.trim().length > 0) {
            return row;
        }

        const lookupId = (typeof row.assessed_by === 'string' ? row.assessed_by : null)
            ?? (typeof row.recorded_by === 'string' ? row.recorded_by : null);

        const assessedByName = lookupId ? staffMap.get(lookupId) : null;
        if (!assessedByName) {
            return row;
        }

        return {
            ...row,
            assessed_by_name: assessedByName,
        };
    });
}

async function enrichWoundRows(rows: WoundRecord[]): Promise<WoundRecord[]> {
    const withResidents = await enrichResidentNames(rows);
    return enrichAssessedByNames(withResidents);
}

async function queryHistoryRows(woundId: string): Promise<WoundRecord[]> {
    const derived = parseDerivedWoundId(woundId);

    if (!derived) {
        const { data, error } = await supabase
            .from('wound_assessments')
            .select('*')
            .eq('wound_id', woundId)
            .order('recorded_at', { ascending: true });

        if (error) throw new Error(error.message);
        return (data ?? []) as WoundRecord[];
    }

    let query = supabase
        .from('wound_assessments')
        .select('*')
        .eq('resident_id', derived.residentId)
        .eq('body_location', derived.bodyLocation)
        .eq('diagnosis_code', derived.diagnosis);

    query = derived.bodyLocationDetail
        ? query.eq('body_location_detail', derived.bodyLocationDetail)
        : query.is('body_location_detail', null);

    const { data, error } = await query.order('recorded_at', { ascending: true });
    if (error) throw new Error(error.message);
    return (data ?? []) as WoundRecord[];
}

function toLiveWritePayload(input: CreateWoundInput, userId?: string, userName?: string): Record<string, unknown> {
    return {
        facility_id: input.facilityId,
        resident_id: input.residentId,
        recorded_by: userId ?? null,
        diagnosis_code: input.diagnosis,
        diagnosis_name: input.diagnosisName ?? DIAGNOSIS_LABELS[input.diagnosis],
        body_location: input.bodyLocation,
        body_location_detail: input.locationDetail ?? null,
        urgency: input.urgency,
        healing_status: input.healingStatus,
        length_cm: input.lengthCm ?? null,
        width_cm: input.widthCm ?? null,
        depth_cm: input.depthCm ?? null,
        wound_bed: input.woundBed ?? null,
        wound_edge: input.woundEdge ?? null,
        exudate_amount: input.exudateAmount ?? null,
        exudate_type: input.exudateType ?? null,
        wound_odor: input.woundOdor ?? null,
        surrounding_skin: input.surroundingSkin ?? null,
        pain_level: input.painLevel ?? null,
        description: input.description ?? null,
        photo_urls: input.photoUrls ?? [],
        products_used: input.productsUsed ?? [],
        notify_doctor: input.notifyDoctor ?? false,
        doctor_name: input.notifyDoctor ? userName ?? null : null,
        recorded_at: new Date().toISOString(),
    };
}

function toLegacyWritePayload(input: CreateWoundInput, userId?: string, userName?: string): Record<string, unknown> {
    return {
        facility_id: input.facilityId,
        resident_id: input.residentId,
        resident_name: input.residentName,
        wound_id: input.woundId ?? null,
        diagnosis: input.diagnosis,
        diagnosis_name: input.diagnosisName ?? DIAGNOSIS_LABELS[input.diagnosis],
        body_location: input.bodyLocation,
        body_location_detail: input.locationDetail ?? null,
        urgency: input.urgency,
        healing_status: input.healingStatus,
        length_cm: input.lengthCm ?? null,
        width_cm: input.widthCm ?? null,
        depth_cm: input.depthCm ?? null,
        wound_bed: input.woundBed ?? null,
        wound_edge: input.woundEdge ?? null,
        exudate_amount: input.exudateAmount ?? null,
        exudate_type: input.exudateType ?? null,
        wound_odor: input.woundOdor ?? null,
        surrounding_skin: input.surroundingSkin ?? null,
        pain_level: input.painLevel ?? null,
        infection_signs: input.infectionSigns ?? false,
        treatment_notes: input.treatmentNotes ?? null,
        treatment_performed: input.treatmentPerformed ?? null,
        dressing_used: input.dressingUsed ?? null,
        next_change_date: input.nextChangeDate ?? null,
        description: input.description ?? null,
        photo_urls: input.photoUrls ?? [],
        products_used: input.productsUsed ?? [],
        notify_doctor: input.notifyDoctor ?? false,
        approval_status: input.notifyDoctor ? 'pending' : null,
        assessed_by: userId,
        assessed_by_name: userName,
    };
}

async function updateWithFallback(
    table: string,
    livePayload: Record<string, unknown>,
    legacyPayload: Record<string, unknown>,
    matcher: { column: string; value: string },
): Promise<void> {
    const liveAttempt = await supabase.from(table).update(livePayload).eq(matcher.column, matcher.value);
    if (!liveAttempt.error) {
        return;
    }

    const legacyAttempt = await supabase.from(table).update(legacyPayload).eq(matcher.column, matcher.value);
    if (legacyAttempt.error) {
        throw new Error(liveAttempt.error.message);
    }
}

// ─── Label Configs ──────────────────────────────────────────────────────────

export const URGENCY_CONFIG: Record<WoundUrgency, { label: string; color: string; bg: string }> = {
    critical: {
        label: "Kritisch",
        color: "text-red-800 dark:text-red-300",
        bg: "bg-red-200 dark:bg-red-950/40",
    },
    green: {
        label: "Gering",
        color: "text-emerald-700 dark:text-emerald-400",
        bg: "bg-emerald-100 dark:bg-emerald-900/30",
    },
    yellow: {
        label: "Mittel",
        color: "text-amber-700 dark:text-amber-400",
        bg: "bg-amber-100 dark:bg-amber-900/30",
    },
    red: {
        label: "Hoch",
        color: "text-red-700 dark:text-red-400",
        bg: "bg-red-100 dark:bg-red-900/30",
    },
};

export const HEALING_CONFIG: Record<HealingStatus, { label: string; color: string }> = {
    verbessert: { label: "Verbessert", color: "text-emerald-600" },
    unveraendert: { label: "Unverändert", color: "text-amber-600" },
    verschlechtert: { label: "Verschlechtert", color: "text-red-600" },
    neu: { label: "Neu", color: "text-brand-600" },
    abgeheilt: { label: "Abgeheilt", color: "text-neutral-500" },
};

export const DIAGNOSIS_LABELS: Record<WoundDiagnosis, string> = {
    dekubitus_grad_1: "Dekubitus Grad 1",
    dekubitus_grad_2: "Dekubitus Grad 2",
    dekubitus_grad_3: "Dekubitus Grad 3",
    dekubitus_grad_4: "Dekubitus Grad 4",
    ulcus_cruris: "Ulcus cruris",
    diabetisches_ulcus: "Diabetisches Ulcus",
    operationswunde: "Operationswunde",
    traumatische_wunde: "Traumatische Wunde",
    verbrennungswunde: "Verbrennungswunde",
    skin_tear: "Skin Tear",
    intertrigo: "Intertrigo",
    andere: "Andere",
};

export const WOUND_BED_LABELS: Record<WoundBed, string> = {
    granulation_rot: "Granulation (rot)",
    fibrin_gelb: "Fibrin (gelb)",
    nekrose_schwarz: "Nekrose (schwarz)",
    epithel_rosa: "Epithel (rosa)",
    gemischt: "Gemischt",
};

export const WOUND_EDGE_LABELS: Record<WoundEdge, string> = {
    flach: "Flach",
    aufgeworfen: "Aufgeworfen",
    unterminiert: "Unterminiert",
    mazeriert: "Mazeriert",
    nekrotisch: "Nekrotisch",
};

export const EXUDATE_AMOUNT_LABELS: Record<ExudateAmount, string> = {
    keine: "Keine",
    gering: "Gering",
    maessig: "Mäßig",
    stark: "Stark",
};

export const EXUDATE_TYPE_LABELS: Record<ExudateType, string> = {
    seroes: "Serös",
    sanguinolent: "Sanguinolent",
    purulent: "Purulent",
    seroes_purulent: "Serös-purulent",
};

export const WOUND_ODOR_LABELS: Record<WoundOdor, string> = {
    kein: "Kein Geruch",
    leicht: "Leicht",
    stark: "Stark",
    uebel: "Übel/faulig",
};

export const SURROUNDING_SKIN_LABELS: Record<SurroundingSkin, string> = {
    intakt: "Intakt",
    geroetet: "Gerötet",
    mazeriert: "Mazeriert",
    trocken_schuppig: "Trocken/schuppig",
    oedematoes: "Ödematös",
};

export const BODY_LOCATION_LABELS: Record<WoundBodyLocation, string> = {
    kopf_gesicht: "Kopf/Gesicht",
    stirn: "Stirn",
    gesicht: "Gesicht",
    ohr_links: "Ohr li.",
    ohr_rechts: "Ohr re.",
    nacken: "Nacken",
    schulter_rechts: "Schulter re.",
    schulter_links: "Schulter li.",
    oberarm_rechts: "Oberarm re.",
    oberarm_links: "Oberarm li.",
    unterarm_rechts: "Unterarm re.",
    unterarm_links: "Unterarm li.",
    hand_rechts: "Hand re.",
    hand_links: "Hand li.",
    finger_rechts: "Finger re.",
    finger_links: "Finger li.",
    brust: "Brust",
    bauch: "Bauch",
    ruecken_oben: "Oberer Rücken",
    ruecken_mitte: "Mittlerer Rücken",
    ruecken_unten: "Unterer Rücken",
    hueftbereich_rechts: "Hüfte re.",
    hueftbereich_links: "Hüfte li.",
    gesaess_rechts: "Gesäß re.",
    gesaess_links: "Gesäß li.",
    steissbein: "Steißbein",
    leiste_rechts: "Leiste re.",
    leiste_links: "Leiste li.",
    oberschenkel_rechts: "Oberschenkel re.",
    oberschenkel_links: "Oberschenkel li.",
    knie_rechts: "Knie re.",
    knie_links: "Knie li.",
    unterschenkel_rechts: "Unterschenkel re.",
    unterschenkel_links: "Unterschenkel li.",
    knoechel_rechts: "Knöchel re.",
    knoechel_links: "Knöchel li.",
    ferse_rechts: "Ferse re.",
    ferse_links: "Ferse li.",
    fuss_rechts: "Fuß re.",
    fuss_links: "Fuß li.",
    zehen_links: "Zehen li.",
    zehen_rechts: "Zehen re.",
    arm_links: "Arm li.",
    arm_rechts: "Arm re.",
    andere: "Andere",
};

/** Body locations grouped by region for form UX */
export const BODY_LOCATION_GROUPS: Array<{ region: string; locations: Array<{ code: WoundBodyLocation; label: string }> }> = [
    { region: "Kopf", locations: [{ code: "kopf_gesicht", label: "Kopf/Gesicht" }, { code: "stirn", label: "Stirn" }, { code: "gesicht", label: "Gesicht" }, { code: "ohr_links", label: "Ohr li." }, { code: "ohr_rechts", label: "Ohr re." }, { code: "nacken", label: "Nacken" }] },
    { region: "Oberkörper", locations: [{ code: "schulter_links", label: "Schulter li." }, { code: "schulter_rechts", label: "Schulter re." }, { code: "brust", label: "Brust" }, { code: "bauch", label: "Bauch" }, { code: "ruecken_oben", label: "Oberer Rücken" }, { code: "ruecken_mitte", label: "Mittlerer Rücken" }, { code: "ruecken_unten", label: "Unterer Rücken" }] },
    { region: "Becken", locations: [{ code: "steissbein", label: "Steißbein" }, { code: "hueftbereich_links", label: "Hüfte li." }, { code: "hueftbereich_rechts", label: "Hüfte re." }, { code: "gesaess_links", label: "Gesäß li." }, { code: "gesaess_rechts", label: "Gesäß re." }, { code: "leiste_links", label: "Leiste li." }, { code: "leiste_rechts", label: "Leiste re." }] },
    { region: "Beine", locations: [{ code: "oberschenkel_links", label: "Oberschenkel li." }, { code: "oberschenkel_rechts", label: "Oberschenkel re." }, { code: "knie_links", label: "Knie li." }, { code: "knie_rechts", label: "Knie re." }, { code: "unterschenkel_links", label: "Unterschenkel li." }, { code: "unterschenkel_rechts", label: "Unterschenkel re." }, { code: "knoechel_links", label: "Knöchel li." }, { code: "knoechel_rechts", label: "Knöchel re." }] },
    { region: "Füße", locations: [{ code: "ferse_links", label: "Ferse li." }, { code: "ferse_rechts", label: "Ferse re." }, { code: "fuss_links", label: "Fuß li." }, { code: "fuss_rechts", label: "Fuß re." }, { code: "zehen_links", label: "Zehen li." }, { code: "zehen_rechts", label: "Zehen re." }] },
    { region: "Arme", locations: [{ code: "arm_links", label: "Arm li." }, { code: "arm_rechts", label: "Arm re." }, { code: "hand_links", label: "Hand li." }, { code: "hand_rechts", label: "Hand re." }, { code: "finger_links", label: "Finger li." }, { code: "finger_rechts", label: "Finger re." }, { code: "oberarm_links", label: "Oberarm li." }, { code: "oberarm_rechts", label: "Oberarm re." }, { code: "unterarm_links", label: "Unterarm li." }, { code: "unterarm_rechts", label: "Unterarm re." }] },
    { region: "Sonstige", locations: [{ code: "andere", label: "Andere" }] },
];

// ─── Queries ────────────────────────────────────────────────────────────────

function mapRow(d: WoundRecord): WoundAssessment {
    const photoUrls: string[] = Array.isArray(d.photo_urls)
        ? (d.photo_urls as string[])
        : d.photo_url
            ? [d.photo_url as string]
            : [];

    const diagnosis = readDiagnosisCode(d);
    const approvalStatus = deriveApprovalStatus(d);

    return {
        id: d.id as string,
        facilityId: d.facility_id as string,
        residentId: d.resident_id as string,
        residentName: deriveResidentName(d),
        woundId: (d.wound_id as string) ?? buildDerivedWoundId(d),
        diagnosis,
        diagnosisName: (d.diagnosis_name as string) ?? null,
        bodyLocation: d.body_location as WoundBodyLocation,
        locationDetail: (d.body_location_detail as string) ?? null,
        urgency: d.urgency as WoundUrgency,
        healingStatus: (d.healing_status as HealingStatus) ?? "neu",
        lengthCm: d.length_cm as number | null,
        widthCm: d.width_cm as number | null,
        depthCm: d.depth_cm as number | null,
        areaCm2: (d.area_cm2 as number) ?? (d.length_cm && d.width_cm ? Math.round(((d.length_cm as number) * (d.width_cm as number)) * 10) / 10 : null),
        woundBed: (d.wound_bed as WoundBed) ?? null,
        woundEdge: (d.wound_edge as WoundEdge) ?? null,
        exudateAmount: (d.exudate_amount as ExudateAmount) ?? null,
        exudateType: (d.exudate_type as ExudateType) ?? null,
        woundOdor: (d.wound_odor as WoundOdor) ?? null,
        surroundingSkin: (d.surrounding_skin as SurroundingSkin) ?? null,
        painLevel: (d.pain_level as number) ?? null,
        infectionSigns: (d.infection_signs as boolean) ?? false,
        treatmentNotes: (d.treatment_notes as string) ?? null,
        treatmentPerformed: (d.treatment_performed as string) ?? null,
        dressingUsed: (d.dressing_used as string) ?? null,
        nextChangeDate: (d.next_change_date as string) ?? null,
        description: (d.description as string) ?? null,
        assessedBy: (d.assessed_by as string) ?? (d.recorded_by as string) ?? '',
        assessedByName: deriveAssessedByName(d),
        photoUrl: (d.photo_url as string) ?? photoUrls[0] ?? null,
        photoUrls,
        productsUsed: Array.isArray(d.products_used) ? (d.products_used as ProductUsage[]) : [],
        notifyDoctor: (d.notify_doctor as boolean) ?? false,
        doctorConfirmed: (d.doctor_confirmed as boolean) ?? false,
        doctorConfirmedAt: (d.doctor_confirmed_at as string) ?? null,
        approvalStatus,
        approvedBy: (d.approved_by as string) ?? (d.doctor_name as string) ?? null,
        recordedAt: (d.recorded_at as string) ?? (d.created_at as string),
        createdAt: d.created_at as string,
        updatedAt: d.updated_at as string,
    };
}

export async function getWounds(
    facilityId: string,
    filters?: {
        urgency?: WoundUrgency;
        residentId?: string;
        healingStatus?: HealingStatus;
        excludeHealed?: boolean;
    },
): Promise<WoundAssessment[]> {
    let q = supabase
        .from("wound_assessments")
        .select("*")
        .eq("facility_id", facilityId)
        .order("created_at", { ascending: false });

    if (filters?.urgency) q = q.eq("urgency", filters.urgency);
    if (filters?.residentId) q = q.eq("resident_id", filters.residentId);
    if (filters?.healingStatus) q = q.eq("healing_status", filters.healingStatus);
    if (filters?.excludeHealed) q = q.neq("healing_status", "abgeheilt");

    const { data, error } = await q.limit(200);
    if (error) throw new Error(error.message);

    const enrichedRows = await enrichWoundRows((data ?? []) as WoundRecord[]);
    return enrichedRows.map(mapRow);
}

export async function getWoundById(id: string): Promise<WoundAssessment | null> {
    const { data, error } = await supabase
        .from("wound_assessments")
        .select("*")
        .eq("id", id)
        .maybeSingle();

    if (error) throw new Error(error.message);
    if (!data) {
        return null;
    }

    const [enriched] = await enrichWoundRows([data as WoundRecord]);
    return mapRow(enriched ?? (data as WoundRecord));
}

/** All assessments for a persistent wound (by wound_id), chronologically */
export async function getWoundHistory(woundId: string): Promise<WoundAssessment[]> {
    const rows = await queryHistoryRows(woundId);
    const enrichedRows = await enrichWoundRows(rows);
    return enrichedRows.map(mapRow);
}

/** Trend data for a persistent wound — area + pain over time */
export async function getWoundTrendData(woundId: string): Promise<WoundTrendPoint[]> {
    const rows = await queryHistoryRows(woundId);

    return rows.map((d) => ({
        date: (d.recorded_at ?? d.created_at) as string,
        lengthCm: (d.length_cm as number) ?? null,
        widthCm: (d.width_cm as number) ?? null,
        depthCm: (d.depth_cm as number) ?? null,
        areaCm2: (d.area_cm2 as number) ?? (d.length_cm && d.width_cm ? Math.round(((d.length_cm as number) * (d.width_cm as number)) * 10) / 10 : null),
        painLevel: (d.pain_level as number) ?? null,
        healingStatus: (d.healing_status as HealingStatus) ?? "neu",
    }));
}

/** Pending approval queue for a facility */
export async function getApprovalQueue(facilityId: string): Promise<WoundAssessment[]> {
    const { data, error } = await supabase
        .from("wound_approval_queue")
        .select("*")
        .eq("facility_id", facilityId)
        .order("created_at", { ascending: false });

    if (error) throw new Error(error.message);
    return (data ?? []).map(mapRow);
}

/** Approve or reject a wound assessment */
export async function approveWound(input: WoundApprovalInput): Promise<void> {
    const isApproval = input.action === "approved";
    const nextStatus = input.action === "approved" ? "approved" : input.action === "modified" ? "pending" : "rejected";

    await updateWithFallback(
        'wound_assessments',
        {
            doctor_confirmed: isApproval,
            doctor_confirmed_at: isApproval ? new Date().toISOString() : null,
            doctor_confirmed_by: isApproval ? input.approverStaffId ?? input.approverUserId ?? null : null,
            doctor_name: input.approverName,
            ...(input.diagnosisCode ? { diagnosis_code: input.diagnosisCode } : {}),
            ...(input.diagnosisName ? { diagnosis_name: input.diagnosisName } : {}),
        },
        {
            doctor_confirmed: isApproval,
            doctor_confirmed_at: isApproval ? new Date().toISOString() : null,
            approval_status: nextStatus,
            approved_by: input.approverName,
            ...(input.diagnosisCode ? { diagnosis: input.diagnosisCode } : {}),
            ...(input.diagnosisName ? { diagnosis_name: input.diagnosisName } : {}),
        },
        { column: 'id', value: input.assessmentId },
    );

    const auditId = await logWoundApproval({
        woundAssessmentId: input.assessmentId,
        facilityId: input.facilityId,
        approverStaffId: input.approverStaffId ?? null,
        approverUserId: input.approverUserId ?? null,
        approverRole: input.approverRole ?? null,
        approverName: input.approverName,
        approverLanr: input.approverLanr ?? null,
        approverSpecialty: input.approverSpecialty ?? null,
        action: input.action,
        oldStatus: input.oldStatus ?? null,
        newStatus: nextStatus,
        diagnosisCode: input.diagnosisCode ?? null,
        diagnosisName: input.diagnosisName ?? null,
        approvalReason: input.approvalReason ?? null,
        notes: input.notes ?? null,
    });

    if (!auditId) {
        logger.warn('woundApprovalAudit missing audit id', { assessmentId: input.assessmentId, action: input.action });
    }
}

/** Upload wound photo to Supabase Storage */
export async function uploadWoundPhoto(
    facilityId: string,
    assessmentId: string,
    file: File,
): Promise<string | null> {
    const ext = file.name.split(".").pop() ?? "jpg";
    const path = `wounds/${facilityId}/${assessmentId}/${Date.now()}.${ext}`;

    const { error } = await supabase.storage
        .from("care-documents")
        .upload(path, file, { contentType: file.type, upsert: false });

    if (error) throw new Error(error.message);

    const { data: urlData } = supabase.storage.from("care-documents").getPublicUrl(path);
    return urlData?.publicUrl ?? null;
}

export function toCareDocumentPath(photoUrl: string): string | null {
    if (!photoUrl.includes('/storage/v1/object/public/care-documents/')) {
        return null;
    }

    const [, path] = photoUrl.split('/storage/v1/object/public/care-documents/');
    return path ? decodeURIComponent(path) : null;
}

export async function deleteWoundPhoto(photoUrl: string): Promise<void> {
    const path = toCareDocumentPath(photoUrl);
    if (!path) {
        throw new Error('Ungültige Foto-URL');
    }

    const { error } = await supabase.storage.from('care-documents').remove([path]);
    if (error) {
        throw new Error(error.message);
    }
}

export async function createWound(
    input: CreateWoundInput,
    userId: string,
    userName: string,
): Promise<WoundAssessment> {
    const livePayload = toLiveWritePayload(input, userId, userName);
    const legacyPayload = toLegacyWritePayload(input, userId, userName);

    let data: WoundRecord | null = null;
    const liveAttempt = await supabase
        .from('wound_assessments')
        .insert(livePayload)
        .select()
        .single();

    if (liveAttempt.error) {
        const legacyAttempt = await supabase
            .from('wound_assessments')
            .insert(legacyPayload)
            .select()
            .single();

        if (legacyAttempt.error) {
            throw new Error(liveAttempt.error.message);
        }

        data = legacyAttempt.data as WoundRecord;
    } else {
        data = liveAttempt.data as WoundRecord;
    }

    const enrichedRows = await enrichWoundRows([
        {
            ...data,
            resident_name: input.residentName,
            assessed_by_name: userName,
        },
    ]);

    return mapRow(enrichedRows[0] ?? {
        ...(data as WoundRecord),
        resident_name: input.residentName,
        assessed_by_name: userName,
    });
}

export async function updateWound(id: string, updates: Partial<CreateWoundInput>): Promise<void> {
    const liveMapped: Record<string, unknown> = {};
    const legacyMapped: Record<string, unknown> = {};

    if (updates.diagnosis !== undefined) {
        liveMapped.diagnosis_code = updates.diagnosis;
        legacyMapped.diagnosis = updates.diagnosis;
    }
    if (updates.diagnosisName !== undefined) {
        liveMapped.diagnosis_name = updates.diagnosisName;
        legacyMapped.diagnosis_name = updates.diagnosisName;
    }
    if (updates.bodyLocation !== undefined) {
        liveMapped.body_location = updates.bodyLocation;
        legacyMapped.body_location = updates.bodyLocation;
    }
    if (updates.locationDetail !== undefined) {
        liveMapped.body_location_detail = updates.locationDetail;
        legacyMapped.body_location_detail = updates.locationDetail;
    }
    if (updates.urgency !== undefined) {
        liveMapped.urgency = updates.urgency;
        legacyMapped.urgency = updates.urgency;
    }
    if (updates.healingStatus !== undefined) {
        liveMapped.healing_status = updates.healingStatus;
        legacyMapped.healing_status = updates.healingStatus;
    }
    if (updates.lengthCm !== undefined) {
        liveMapped.length_cm = updates.lengthCm;
        legacyMapped.length_cm = updates.lengthCm;
    }
    if (updates.widthCm !== undefined) {
        liveMapped.width_cm = updates.widthCm;
        legacyMapped.width_cm = updates.widthCm;
    }
    if (updates.depthCm !== undefined) {
        liveMapped.depth_cm = updates.depthCm;
        legacyMapped.depth_cm = updates.depthCm;
    }
    if (updates.woundBed !== undefined) {
        liveMapped.wound_bed = updates.woundBed;
        legacyMapped.wound_bed = updates.woundBed;
    }
    if (updates.woundEdge !== undefined) {
        liveMapped.wound_edge = updates.woundEdge;
        legacyMapped.wound_edge = updates.woundEdge;
    }
    if (updates.exudateAmount !== undefined) {
        liveMapped.exudate_amount = updates.exudateAmount;
        legacyMapped.exudate_amount = updates.exudateAmount;
    }
    if (updates.exudateType !== undefined) {
        liveMapped.exudate_type = updates.exudateType;
        legacyMapped.exudate_type = updates.exudateType;
    }
    if (updates.woundOdor !== undefined) {
        liveMapped.wound_odor = updates.woundOdor;
        legacyMapped.wound_odor = updates.woundOdor;
    }
    if (updates.surroundingSkin !== undefined) {
        liveMapped.surrounding_skin = updates.surroundingSkin;
        legacyMapped.surrounding_skin = updates.surroundingSkin;
    }
    if (updates.painLevel !== undefined) {
        liveMapped.pain_level = updates.painLevel;
        legacyMapped.pain_level = updates.painLevel;
    }
    if (updates.infectionSigns !== undefined) {
        legacyMapped.infection_signs = updates.infectionSigns;
    }
    if (updates.treatmentNotes !== undefined) {
        legacyMapped.treatment_notes = updates.treatmentNotes;
    }
    if (updates.treatmentPerformed !== undefined) {
        legacyMapped.treatment_performed = updates.treatmentPerformed;
    }
    if (updates.dressingUsed !== undefined) {
        legacyMapped.dressing_used = updates.dressingUsed;
    }
    if (updates.nextChangeDate !== undefined) {
        legacyMapped.next_change_date = updates.nextChangeDate;
    }
    if (updates.description !== undefined) {
        liveMapped.description = updates.description;
        legacyMapped.description = updates.description;
    }
    if (updates.photoUrls !== undefined) {
        liveMapped.photo_urls = updates.photoUrls;
        legacyMapped.photo_urls = updates.photoUrls;
    }
    if (updates.productsUsed !== undefined) {
        liveMapped.products_used = updates.productsUsed;
        legacyMapped.products_used = updates.productsUsed;
    }
    if (updates.notifyDoctor !== undefined) {
        liveMapped.notify_doctor = updates.notifyDoctor;
        legacyMapped.notify_doctor = updates.notifyDoctor;
        legacyMapped.approval_status = updates.notifyDoctor ? 'pending' : null;
    }

    await updateWithFallback('wound_assessments', liveMapped, legacyMapped, { column: 'id', value: id });
}

export async function getWoundStats(facilityId: string): Promise<{
    total: number;
    critical: number;
    red: number;
    yellow: number;
    green: number;
    urgent: number;
    improving: number;
    worsening: number;
    pendingApproval: number;
    healed: number;
}> {
    const { data, error } = await supabase
        .from("wound_assessments")
        .select("urgency, healing_status, doctor_confirmed, notify_doctor")
        .eq("facility_id", facilityId);

    if (error) throw new Error(error.message);

    const rows = data ?? [];
    return {
        total: rows.length,
        critical: rows.filter((r) => r.urgency === "critical").length,
        red: rows.filter((r) => r.urgency === "red").length,
        yellow: rows.filter((r) => r.urgency === "yellow").length,
        green: rows.filter((r) => r.urgency === "green").length,
        urgent: rows.filter((r) => r.urgency === "red" || r.urgency === "critical").length,
        improving: rows.filter((r) => r.healing_status === "verbessert").length,
        worsening: rows.filter((r) => r.healing_status === "verschlechtert").length,
        pendingApproval: rows.filter((r) => r.notify_doctor && !r.doctor_confirmed).length,
        healed: rows.filter((r) => r.healing_status === "abgeheilt").length,
    };
}
